import { useSelectedOrgUnit } from '@retail/app/stores/selected-context';
import { useFetchAssortmentTags } from '@retail/my-assortment/data-access';
import { AssortmentTag } from '@retail/my-assortment/types';
import {
  AddOrEditItemToAssortmentTagDialog,
  AssortmentTagTable,
  AssortmentTagWithSupplierItemId,
} from '@retail/products/components';
import {
  SupplierItemWithAssortmentTags,
  UpdateOrCreateSupplierItemTag,
} from '@retail/products/types';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { DeleteItemFromAssortmentTag } from './DeleteItemFromAssortmentTag';
import AddItemToAssortment from '../AddItemToAssortment';
import {
  useCreateOrUpdateSupplierItemAssortmentTags,
  useFetchSupplierItemAssortmentTags,
} from '@retail/products/data-access';

interface Props {
  mgItemNumber: number;
}

export function AssortmentTags({ mgItemNumber }: Props) {
  const { assortmentId } = useSelectedOrgUnit();
  const queryClient = useQueryClient();

  const { data: itemAssortmentTagsDTO } = useFetchSupplierItemAssortmentTags({
    mgItemNumber,
    assortmentId,
  });

  const supplierItemsWithTags = useMemo(
    () => itemAssortmentTagsDTO?.mgSupplierItems.map(SupplierItemWithAssortmentTags.fromDto) ?? [],
    [itemAssortmentTagsDTO?.mgSupplierItems]
  );

  const { mutateAsync: postCreateOrUpdateAsync } = useCreateOrUpdateSupplierItemAssortmentTags();
  const addSupplierItemTag = useCallback(
    (tagId: number, supplierItemTag: UpdateOrCreateSupplierItemTag) =>
      postCreateOrUpdateAsync({
        urlVariables: { tagId, assortmentId },
        body: supplierItemTag,
      }).then(() => {
        queryClient.invalidateQueries({ queryKey: ['mg-item-assortment-tags'] });
        queryClient.invalidateQueries({ queryKey: ['assortmentItems'] });
        queryClient.invalidateQueries({ queryKey: ['central-assortments'] });
      }),
    [assortmentId, postCreateOrUpdateAsync, queryClient]
  );

  const { data: assortmentTagDTOS = [] } = useFetchAssortmentTags({
    assortmentIds: [assortmentId],
  });
  const assortmentTags = assortmentTagDTOS.map(AssortmentTag.fromDTO);

  const [addAssortmentTag, setAssortmentTagToAdd] = useState<
    undefined | AssortmentTagWithSupplierItemId
  >(undefined);
  const [editAssortmentTag, setAssortmentTagToEdit] = useState<
    undefined | AssortmentTagWithSupplierItemId
  >(undefined);
  const [supplierItemToAddToAssortment, setSupplierItemToAddToAssortment] = useState<
    SupplierItemWithAssortmentTags | undefined
  >(undefined);
  const [deleteItemAssortmentTag, setItemAssortmentTagToDelete] = useState<
    { tag: AssortmentTag; mgSupplierItemId: number } | undefined
  >(undefined);

  const sortedSupplierItems = [...supplierItemsWithTags].sort((a, b) => {
    return a.supplier.name.localeCompare(b.supplier.name, 'no', { sensitivity: 'base' });
  });
  return (
    <>
      <AssortmentTagTable
        supplierItemsTags={sortedSupplierItems}
        assortmentTags={assortmentTags}
        setAssortmentTagToAdd={setAssortmentTagToAdd}
        setAssortmentTagToEdit={setAssortmentTagToEdit}
        setItemAssortmentTagToDelete={(tag, mgSupplierItemId) =>
          setItemAssortmentTagToDelete({ tag, mgSupplierItemId })
        }
        setSupplierItemToAddToAssortment={setSupplierItemToAddToAssortment}
      />
      {supplierItemToAddToAssortment && (
        <AddItemToAssortment
          open
          mgItemNumber={mgItemNumber}
          supplierId={supplierItemToAddToAssortment.supplier.supplierNumber}
          onClose={() => setSupplierItemToAddToAssortment(undefined)}
        />
      )}
      {addAssortmentTag && (
        <AddOrEditItemToAssortmentTagDialog
          action="add"
          itemToAssortmentTag={addAssortmentTag}
          addSupplierItemTag={(supplierItemTag) =>
            addSupplierItemTag(addAssortmentTag.assortmentTag.id, supplierItemTag)
          }
          onClose={() => setAssortmentTagToAdd(undefined)}
        />
      )}
      {editAssortmentTag && (
        <AddOrEditItemToAssortmentTagDialog
          action="edit"
          itemToAssortmentTag={editAssortmentTag}
          addSupplierItemTag={(supplierItemTag) =>
            addSupplierItemTag(editAssortmentTag.assortmentTag.id, supplierItemTag)
          }
          onClose={() => setAssortmentTagToEdit(undefined)}
        />
      )}
      {deleteItemAssortmentTag && (
        <DeleteItemFromAssortmentTag
          deleteItemAssortmentTag={deleteItemAssortmentTag.tag}
          supplierItemId={deleteItemAssortmentTag.mgSupplierItemId}
          assortmentId={assortmentId}
          onClose={() => setItemAssortmentTagToDelete(undefined)}
        />
      )}
    </>
  );
}
